/* eslint-disable react-hooks/rules-of-hooks */
import {
    deleteProcessDiagramPreferences,
    getProcessDiagramPreferences,
    getProcessInsightDiagram,
    getReportSeriesData,
    getSankey,
    listProcessDiagramPreferences,
    listProcesses,
    saveProcessDiagramPreferences,
    uiDiagramRequest,
} from './processesApi'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { getWorkspaceID } from '../../../utils'
import { useSelector } from 'react-redux'
import { useLazyQuery } from '../../../hooks/useLazyQuery'

export const useProcesses = () => {
    const workspaceID = getWorkspaceID()
    const isGuestMode = useSelector((state) => state.auth.isGuestMode)

    const {
        data: processes,
        error,
        isLoading: listProcessLoading,
    } = useQuery({
        queryKey: ['processes', workspaceID],
        queryFn: listProcesses,
    })

    const { data: reportSeriesData, isLoading: isLoadingReportSeries } = useQuery({
        queryKey: ['reportSeriesData', workspaceID],
        queryFn: getReportSeriesData,
    })

    // TODO:  Delete this on remove the Old Processes component
    const { mutate: uiDiagramMutation, isPending: isLoadingUiDiagram } = useMutation({
        mutationFn: (data) => uiDiagramRequest(data),
        onSuccess: (response, variables) => {
            variables?.onSuccess(response)
        },
        onError: (error, variables) => {
            variables?.onError(error)
        },
    })

    /////

    const { mutate: saveDiagramMutation, isPending: isLoadingSaveDiagramMutation } = useMutation({
        mutationFn: ({ data }) => saveProcessDiagramPreferences(data, isGuestMode),
        onSuccess: (response, variables) => {
            variables?.onSuccess(response)
        },
        onError: (error, variables) => {
            variables?.onError(error)
        },
    })

    const { mutate: deleteDiagramMutation, isPending: isLoadingDeleteDiagramMutation } =
        useMutation({
            mutationFn: ({ data }) => deleteProcessDiagramPreferences(data, isGuestMode),
            onSuccess: (response, variables) => {
                variables?.onSuccess(response)
            },
            onError: (error, variables) => {
                variables?.onError(error)
            },
        })

    const { isPending: isPendingGetSankey, mutate: getSankeyMutation } = useMutation({
        mutationFn: ({ payload }) => getSankey(payload),
        onSuccess: (response, variables) => {
            variables?.onSuccess(response)
        },
        onError: (error, variables) => {
            variables?.onError(error)
        },
    })

    const {
        mutate: getProcessInsightDiagramMutation,
        isPending: isPendingGetProcessInsightDiagram,
    } = useMutation({
        mutationFn: ({ payload }) => getProcessInsightDiagram(payload, isGuestMode),
        onSuccess: (response, variables) => {
            variables?.onSuccess(response)
        },
        onError: (error, variables) => {
            variables?.onError(error)
        },
    })

    const useProcessDiagramPreferences = (diagramName) => {
        const {
            isLoading: isLoadingProcessDiagramPreferences,
            fetch: fetchProcessDiagramPreferences,
        } = useLazyQuery({
            queryKey: ['processDiagramPreferences', diagramName],
            queryFn: () => getProcessDiagramPreferences(diagramName, isGuestMode),
        })

        return {
            isLoadingProcessDiagramPreferences,
            fetchProcessDiagramPreferences,
        }
    }

    const useListProcessDiagramPreferences = () => {
        const {
            isLoading: isLoadingProcessDiagramPreferences,
            data: dataProcessDiagramPreferences,
        } = useQuery({
            queryKey: ['processDiagramPreferences'],
            queryFn: () => listProcessDiagramPreferences(isGuestMode),
        })

        return {
            isLoadingProcessDiagramPreferences,
            dataProcessDiagramPreferences,
        }
    }

    const stableProcesses = useMemo(() => processes?.processes || [], [processes])
    const stableReportSeries = useMemo(() => reportSeriesData?.tables || [], [reportSeriesData])

    return {
        processes: stableProcesses,
        error,
        listProcessLoading,
        reportSeriesData: stableReportSeries,
        isLoadingReportSeries,
        uiDiagramMutation,
        isLoadingUiDiagram,

        //Mutations
        saveDiagramMutation,
        isLoadingSaveDiagramMutation,
        deleteDiagramMutation,
        isLoadingDeleteDiagramMutation,
        isPendingGetSankey,
        getSankeyMutation,
        isPendingGetProcessInsightDiagram,
        getProcessInsightDiagramMutation,

        // Query
        useProcessDiagramPreferences,
        useListProcessDiagramPreferences,
    }
}
